import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode } from 'swiper/modules';
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import { GET_VOLUME_PRICING } from '@/graphql/queries';
import 'swiper/css';
import 'swiper/css/free-mode';

const USD_RATE = 95; // 1 USD = 95 INR (your base conversion)

const DiscountBanner = () => {
  const selectedCurrency = useSelector((state) => state.currency.selectedCurrency);
  const conversionRate = selectedCurrency?.conversion_rate || 1;
  const currencySymbol = selectedCurrency?.symbol || '₹';

  const { data, loading, error } = useQuery(GET_VOLUME_PRICING, {
    variables: { where: { type: { _eq: 'VOLUME_PRICING' } } },
    fetchPolicy: 'network-only',
  });

  // minimum_purchase is stored in USD.
  // Rate system: INR=1 (base), USD=95, GBP=128, etc.
  // Formula: usdAmount * USD_RATE / conversionRate
  // USD:  1000 * 95 / 95  = $1,000  ✅
  // INR:  1000 * 95 / 1   = ₹95,000 ✅
  // GBP:  1000 * 95 / 128 = £742    ✅
  const convertAmount = (usdAmount) => {
    const inInr = usdAmount * USD_RATE;
    const converted = (inInr / conversionRate).toFixed(0);
    return `${currencySymbol}${Number(converted).toLocaleString()}`;
  };

  const discountTiers =
    data?.tenant_org_web_configuration?.[0]?.web_configuration_meta?.map((item) => ({
      id: item.priority, // no id field in query, priority is unique
      discount: `${item.data_values?.discount_percentage}%`,
      usdAmount: item.data_values?.minimum_purchase,
      description: 'minimum purchase',
    })) || [];

  const TierCard = ({ tier, index }) => (
    <div
      className="group relative h-full"
      style={{ animation: `fadeInUp 0.7s ease-out ${index * 0.1}s backwards` }}
    >
      <div
        className="relative h-full rounded-2xl transition-all duration-500 hover:-translate-y-2"
        style={{
          background: 'linear-gradient(160deg, #ffffff 0%, #f7ede3 55%, #efe0d1 100%)',
          border: '1px solid rgba(100,72,60,0.18)',
          boxShadow: '0 1px 0 rgba(255,255,255,0.95) inset, 0 4px 18px rgba(100,72,60,0.09)',
        }}
      >
        {/* Hover glow */}
        <div
          className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{
            boxShadow: '0 20px 50px -12px rgba(100,72,60,0.22), 0 0 0 1px rgba(197,160,138,0.35)',
          }}
        />

        <div className="relative z-10 p-4 sm:p-6 lg:p-8">
          {/* Discount percentage */}
          <div className="flex items-baseline justify-center mb-4 sm:mb-6">
            <span
              className="font-light tracking-tight"
              style={{
                fontSize: 'clamp(2.5rem, 4vw, 3.5rem)',
                color: '#3a2015',
                lineHeight: 1,
              }}
            >
              {tier.discount}
            </span>
            <span
              className="text-[10px] sm:text-sm ml-1.5 uppercase tracking-wider font-light"
              style={{ color: 'rgba(100,72,60,0.55)' }}
            >
              OFF
            </span>
          </div>

          {/* Divider with diamond centre */}
          <div className="flex items-center gap-2 mb-4 sm:mb-6">
            <div
              className="flex-1 h-px"
              style={{ background: 'linear-gradient(to right, transparent, rgba(100,72,60,0.22))' }}
            />
            <div
              className="w-1 h-1 rotate-45 flex-shrink-0"
              style={{ background: 'rgba(168,112,78,0.55)' }}
            />
            <div
              className="flex-1 h-px"
              style={{ background: 'linear-gradient(to left, transparent, rgba(100,72,60,0.22))' }}
            />
          </div>

          {/* Minimum purchase amount */}
          <div className="text-center space-y-1 sm:space-y-2">
            <div
              className="inline-block px-3 py-1 rounded-sm"
              style={{
                background: 'linear-gradient(135deg, #64483c 0%, #a8704e 100%)',
                boxShadow: '0 2px 10px rgba(100,72,60,0.25)',
              }}
            >
              <p
                className="font-semibold tracking-tight text-white"
                style={{ fontSize: 'clamp(1.2rem, 2vw, 1.5rem)' }}
              >
                {convertAmount(tier.usdAmount)}
              </p>
            </div>
            <p
              className="text-[9px] sm:text-xs uppercase tracking-wider font-bold"
              style={{ color: 'rgba(100,72,60,0.55)' }}
            >
              {tier.description}
            </p>
          </div>
        </div>

        {/* Bottom accent bar */}
        <div
          className="absolute bottom-0 left-0 right-0 h-0.5 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 rounded-b-2xl"
          style={{ background: 'linear-gradient(90deg, #64483c, #a8704e, #c5a08a)' }}
        />
      </div>

      {/* Corner accents */}
      <div className="absolute -top-1 -left-1 w-3 h-3 border-t border-l border-[#64483c]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="absolute -bottom-1 -right-1 w-3 h-3 border-b border-r border-[#64483c]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    </div>
  );

  // ── Loading skeleton ──
  if (loading) {
    return (
      <section className="relative w-full overflow-hidden py-10 sm:py-14 lg:py-16 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-10 lg:mb-12">
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse mx-auto mb-4" />
          <div className="h-10 w-64 bg-gray-200 rounded animate-pulse mx-auto mb-3" />
          <div className="h-4 w-80 bg-gray-200 rounded animate-pulse mx-auto" />
        </div>
        <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-5 gap-5 lg:gap-8 max-w-6xl mx-auto">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="rounded-2xl h-44 bg-gradient-to-br from-amber-50 to-rose-50 animate-pulse"
            />
          ))}
        </div>
      </section>
    );
  }

  // ── Error / empty states ──
  if (error || discountTiers.length === 0) return null;

  return (
    <section className="relative w-full overflow-hidden bg-gradient-to-br">
      {/* Decorative blobs */}
      <div className="absolute inset-0 opacity-[0.015] pointer-events-none">
        <div className="absolute top-0 left-0 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] rounded-full bg-[#64483c] blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[350px] sm:w-[600px] h-[350px] sm:h-[600px] rounded-full bg-[#a8704e] blur-[140px]" />
      </div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-16">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10 lg:mb-12">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="h-px w-8 sm:w-12 bg-gradient-to-r from-transparent via-[#64483c] to-transparent" />
            <span className="text-[10px] sm:text-xs tracking-[0.25em] sm:tracking-[0.3em] uppercase font-light text-[#64483c]">
              Volume Pricing
            </span>
            <div className="h-px w-8 sm:w-12 bg-gradient-to-r from-transparent via-[#64483c] to-transparent" />
          </div>

          <h2
            className="text-2xl sm:text-4xl lg:text-5xl font-light mb-3 sm:mb-4 tracking-tight"
            style={{ color: '#422819' }}
          >
            Exceptional Value
          </h2>

          <p className="text-sm sm:text-base lg:text-lg text-[#64483c]/70 max-w-xl mx-auto font-light leading-relaxed px-2">
            Invest in quality and elegance. Discover our exclusive volume discounts designed for
            discerning collectors.
          </p>
        </div>

        {/* Desktop grid */}
        <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-5 gap-5 lg:gap-8 max-w-6xl mx-auto">
          {discountTiers.map((tier, index) => (
            <TierCard key={tier.id} tier={tier} index={index} />
          ))}
        </div>

        {/* Mobile swiper */}
        <div className="md:hidden -mx-4">
          <Swiper
            modules={[FreeMode]}
            spaceBetween={12}
            slidesPerView={1.8}
            freeMode={true}
            grabCursor={true}
            style={{ paddingLeft: '16px', paddingRight: '16px' }}
          >
            {discountTiers.map((tier, index) => (
              <SwiperSlide key={tier.id}>
                <TierCard tier={tier} index={index} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default DiscountBanner;